import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "@mui/material";
import { Html5Qrcode } from "html5-qrcode";
import { btnDelay, usePlaySound } from "common/utils";
import { Spin, FadeTransition } from "common/elements";
import i18n from "features/parent/i18n";
import { approveQrLogin, getQrLoginSessionInfo } from "features/auth/api";

const CODE_PATTERN = /^[A-HJ-NP-Z2-9]{10}$/;

const normalizeCode = (raw: string) =>
  raw
    .trim()
    .toUpperCase()
    .replace(/^MANAPZL:LOGIN:/, "")
    .replace(/[-\s]/g, "");

enum ApproveStep {
  Input = "input",
  Confirm = "confirm",
  Done = "done",
}

enum QrMode {
  Scan = "scan",
  Code = "code",
}

export const QrLoginApproveModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const [step, setStep] = useState<ApproveStep>(ApproveStep.Input);
  const [mode, setMode] = useState<QrMode>(QrMode.Scan);
  const [codeFirst, setCodeFirst] = useState("");
  const [codeSecond, setCodeSecond] = useState("");
  const [pendingCode, setPendingCode] = useState("");
  const [requestingApp, setRequestingApp] = useState<"kids" | "parents" | "">("");
  const [error, setError] = useState("");
  const [cameraError, setCameraError] = useState(false);
  const [loading, setLoading] = useState(false);
  // セッション情報照会中（スキャン/コード送信 → 確認遷移の間）の表示用
  const [infoLoading, setInfoLoading] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  const secondRef = useRef<HTMLInputElement>(null);
  // スキャン中はデコードが連続発火するため、セッション情報取得の多重実行を防ぐ
  const infoLoadingRef = useRef(false);

  const handleClose = () => {
    setStep(ApproveStep.Input);
    setMode(QrMode.Scan);
    setCodeFirst("");
    setCodeSecond("");
    setPendingCode("");
    setRequestingApp("");
    setError("");
    setCameraError(false);
    setInfoLoading(false);
    onClose();
  };

  // 確認ステップへ進む前に、コードのリクエスト元アプリをサーバーから取得する
  const goConfirm = useCallback(async (normalized: string) => {
    if (infoLoadingRef.current) return;
    infoLoadingRef.current = true;
    setInfoLoading(true);
    try {
      const response = await getQrLoginSessionInfo({ code: normalized });
      setRequestingApp(response.data.app);
      setPendingCode(normalized);
      setError("");
      setStep(ApproveStep.Confirm);
    } catch (err) {
      console.error(err);
      setError(i18n.t("MSG_QR_LOGIN_SCAN_INVALID"));
    } finally {
      infoLoadingRef.current = false;
      setInfoLoading(false);
    }
  }, []);

  // カメラスキャン（入力ステップ・スキャンモードのときのみ）
  // MUI Dialog のマウント遅延で要素が未生成のことがあるため、出現を待ってから初期化する。
  // Html5Qrcode のコンストラクタは要素が無いと同期例外を投げるので try/catch でも保護する。
  useEffect(() => {
    if (!open || step !== ApproveStep.Input || mode !== QrMode.Scan) return;
    let scanner: Html5Qrcode | null = null;
    let cancelled = false;
    let rafId = 0;
    let tries = 0;

    const init = () => {
      if (cancelled) return;
      if (!document.getElementById("qr-login-approve-reader")) {
        if (tries < 30) {
          tries += 1;
          rafId = requestAnimationFrame(init);
        } else {
          setCameraError(true);
        }
        return;
      }
      try {
        scanner = new Html5Qrcode("qr-login-approve-reader");
        scanner
          .start(
            { facingMode: "environment" },
            { fps: 10 },
            (decodedText) => {
              const normalized = normalizeCode(decodedText);
              if (CODE_PATTERN.test(normalized)) {
                goConfirm(normalized);
              } else {
                setError(i18n.t("MSG_QR_LOGIN_SCAN_INVALID"));
              }
            },
            undefined,
          )
          .then(() => {
            // 注入された video を画面いっぱいに（object-cover）
            const video = document
              .getElementById("qr-login-approve-reader")
              ?.querySelector("video");
            if (video) {
              video.style.width = "100%";
              video.style.height = "100%";
              video.style.objectFit = "cover";
            }
          })
          .catch((err) => {
            console.error(err);
            setCameraError(true);
          });
      } catch (err) {
        console.error(err);
        setCameraError(true);
      }
    };
    rafId = requestAnimationFrame(init);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (scanner && scanner.isScanning) {
        scanner.stop().catch(() => undefined);
      }
    };
  }, [open, step, mode, goConfirm]);

  const handleFirstChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setCodeFirst(cleaned.slice(0, 5));
    if (cleaned.length > 5) {
      setCodeSecond(cleaned.slice(5, 10));
      secondRef.current?.focus();
    } else if (cleaned.length === 5) {
      secondRef.current?.focus();
    }
  };

  const handleSecondChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setCodeSecond(
      e.target.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 5),
    );
  };

  const handleCodeSubmit = () => {
    play();
    const normalized = normalizeCode(codeFirst + codeSecond);
    if (!CODE_PATTERN.test(normalized)) {
      setError(i18n.t("MSG_QR_LOGIN_SCAN_INVALID"));
      return;
    }
    goConfirm(normalized);
  };

  const handleApprove = () => {
    if (loading) return;
    setLoading(true);
    setError("");
    btnDelay(async () => {
      try {
        await approveQrLogin({ code: pendingCode });
        setStep(ApproveStep.Done);
      } catch (err) {
        console.error(err);
        setError(i18n.t("MSG_QR_LOGIN_SCAN_INVALID"));
        setStep(ApproveStep.Input);
        setCodeFirst("");
        setCodeSecond("");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleBackToInput = () => {
    setStep(ApproveStep.Input);
    setPendingCode("");
    setRequestingApp("");
    setError("");
  };

  const tabClass = (active: boolean) =>
    `flex-1 flex-row-el flex-center py-2 rounded-lg !whitespace-nowrap cursor-pointer border-none type-inherit-font type-text type-sm transition-all ${
      active ? "bg-white text-textcolor-black shadow-sm" : "bg-transparent text-gray2-100"
    }`;

  const inputClass =
    "w-[130px] box-border py-3 px-2 rounded-xl text-center tracking-widest bg-white border-2 border-solid border-gray2-60 focus:border-blue-100 outline-none type-inherit-font type-text type-body disabled:opacity-50";

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="relative flex-col-el w-full h-full bg-white">
        {step === ApproveStep.Done ? (
          <div className="flex-col-el flex-center flex-1 w-full gap-4 p-6">
            <span className="material-symbols-outlined type-display text-safe">check_circle</span>
            <p className="type-text type-body text-textcolor-black text-center">
              {i18n.t("MSG_QR_LOGIN_APPROVE_SUCCESS")}
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="w-[300px] py-3 bg-ink text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all type-inherit-font type-text type-sm"
            >
              {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
            </button>
          </div>
        ) : step === ApproveStep.Confirm ? (
          <div className="flex-col-el flex-1 w-full p-6">
            <div className="flex-col-el flex-center flex-1 w-full gap-4">
              <span className="material-symbols-outlined type-display text-gray2-60">error</span>
              <p className="type-text type-body text-textcolor-black text-center">
                {i18n.t("MSG_QR_LOGIN_APPROVE_CONFIRM")}
              </p>
              <p className="type-text type-body text-textcolor-black tracking-widest">
                {`${pendingCode.slice(0, 5)}-${pendingCode.slice(5)}`}
              </p>
              <p className="type-text type-sm text-gray2-100">
                {i18n.t("MSG_QR_LOGIN_APPROVE_CONFIRM_DESC", {
                  app: i18n.t(
                    requestingApp === "kids"
                      ? "MSG_QR_LOGIN_APP_NAME_KIDS"
                      : "MSG_QR_LOGIN_APP_NAME_PARENTS",
                  ),
                })}
              </p>
              {error && <p className="type-text type-sm text-danger text-center">{error}</p>}
            </div>
            <div className="flex-col-el items-center w-full gap-2 shrink-0">
              <button
                type="button"
                onClick={handleApprove}
                disabled={loading}
                className="w-full max-w-[480px] py-3 bg-green-100 text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 type-inherit-font type-text type-sm"
              >
                {i18n.t("MSG_SIGN_IN_BTN")}
              </button>
              <button
                type="button"
                onClick={handleBackToInput}
                disabled={loading}
                className="w-full max-w-[480px] py-3 bg-white box-border border-solid border border-gray2-60 text-textcolor-black rounded-xl cursor-pointer hover:bg-gray2-20 transition-all disabled:opacity-50 type-inherit-font type-text type-sm"
              >
                {i18n.t("MSG_CANCEL_BTN")}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 上部: scan=カメラ / code=白・中央 */}
            <div
              className={`relative flex-1 w-full overflow-hidden ${
                mode === QrMode.Scan ? "bg-textcolor-black" : "bg-white"
              }`}
            >
              {mode === QrMode.Scan ? (
                cameraError ? (
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <p className="type-text type-body text-white text-center">
                      {i18n.t("MSG_QR_LOGIN_SCAN_CAMERA_ERROR")}
                    </p>
                  </div>
                ) : (
                  <>
                    <div id="qr-login-approve-reader" className="absolute inset-0 w-full h-full" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-[75%] max-w-[380px] aspect-square rounded-2xl shadow-[0_0_28px_9999px_#00000099]" />
                    </div>
                  </>
                )
              ) : (
                <div className="flex-col-center w-full h-full gap-4 p-6">
                  <label className="type-text type-sm text-textcolor-black">
                    {i18n.t("MSG_QR_LOGIN_SCAN_MANUAL_LABEL")}
                  </label>
                  <div className="flex-row-el items-center justify-center gap-2">
                    <input
                      ref={firstRef}
                      value={codeFirst}
                      onChange={handleFirstChange}
                      placeholder="ABCDE"
                      maxLength={5}
                      className={inputClass}
                    />
                    <span className="type-text type-title text-gray2-100">-</span>
                    <input
                      ref={secondRef}
                      value={codeSecond}
                      onChange={handleSecondChange}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !codeSecond) {
                          firstRef.current?.focus();
                        }
                      }}
                      placeholder="FGHJK"
                      maxLength={5}
                      className={inputClass}
                    />
                  </div>
                  {error && <p className="type-text type-sm text-danger text-center">{error}</p>}
                  <button
                    type="button"
                    className="w-[300px] py-3 bg-green-100 text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 type-inherit-font type-text type-sm"
                    onClick={handleCodeSubmit}
                    disabled={infoLoading || (codeFirst + codeSecond).length < 10}
                  >
                    {i18n.t("MSG_SIGN_IN_BTN")}
                  </button>
                </div>
              )}

              {/* スキャン中のエラーバナー（カメラ上に重ねる。承認失敗で戻ったときも見える） */}
              {mode === QrMode.Scan && error && (
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center pointer-events-none">
                  <p className="bg-white/90 text-danger rounded-xl px-4 py-2 type-text type-sm">
                    {error}
                  </p>
                </div>
              )}
            </div>

            {/* 下部（白）: 説明＋モード切替タブ */}
            <div className="flex-col-el items-center gap-3 px-2 py-4 border-t border-outline shrink-0 bg-white">
              <p className="type-text type-sm text-gray2-100 text-center">
                {i18n.t("MSG_QR_LOGIN_APPROVE_DESC")}
              </p>
              <div className="flex-row-el w-full box-border p-1 gap-1 bg-gray2-30 rounded-xl">
                <button
                  type="button"
                  className={tabClass(mode === QrMode.Scan)}
                  onClick={() => setMode(QrMode.Scan)}
                >
                  {i18n.t("MSG_QR_LOGIN_SWITCH_TO_SCAN")}
                </button>
                <button
                  type="button"
                  className={tabClass(mode === QrMode.Code)}
                  onClick={() => setMode(QrMode.Code)}
                >
                  {i18n.t("MSG_QR_LOGIN_SCAN_MANUAL_LABEL")}
                </button>
              </div>
            </div>
          </>
        )}

        {/* 全面ローディング（照会・承認中の連打防止。×は z-[100] で閉じる操作のみ可能） */}
        {(infoLoading || loading) && (
          <div className="absolute inset-0 z-[90] flex items-center justify-center bg-white/60">
            <Spin />
          </div>
        )}

        {/* × 閉じる（常に最前面・左上。html5-qrcode の重なりに影響されないようルート直下に固定） */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 z-[100] flex items-center justify-center w-[48px] h-[48px] rounded-full bg-black/30 border-none cursor-pointer type-inherit-font"
        >
          <span className="material-symbols-outlined type-title text-white">close</span>
        </button>
      </div>
    </Dialog>
  );
};
